/**
 * Sync UI across browser tabs via shared IndexedDB + BroadcastChannel.
 */
import type { StyxFrame } from "./frame";
import type { ConnectionState } from "./styxClient";

export type TabSyncMsg =
  | { type: "messages-changed"; peerId: string }
  | { type: "contacts-changed" }
  | { type: "idb-changed"; scope: "messages"; peerId: string }
  | { type: "idb-changed"; scope: "contacts" }
  | { type: "ws-command"; frameType: string; payload: Record<string, unknown>; id?: string }
  | { type: "ws-frame"; frame: StyxFrame }
  | { type: "ws-state"; conn: ConnectionState; authenticated: boolean; leader: boolean };

const CHANNEL_NAME = "styx-tab-sync";

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!channel) channel = new BroadcastChannel(CHANNEL_NAME);
  return channel;
}

export function broadcastTabSync(msg: TabSyncMsg): void {
  try {
    getChannel()?.postMessage(msg);
  } catch {
    /* ignore */
  }
}

export function subscribeTabSync(handler: (msg: TabSyncMsg) => void): () => void {
  const ch = getChannel();
  if (!ch) return () => {};
  const fn = (ev: MessageEvent) => {
    const data = ev.data as TabSyncMsg;
    if (data && typeof data.type === "string") handler(data);
  };
  ch.addEventListener("message", fn);
  return () => ch.removeEventListener("message", fn);
}
